import { useState } from 'react'

import { runInAction } from 'mobx'
import type moment from 'moment'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useEffect } from 'modules/core/hooks/useEffect'
import asyncAction from 'modules/core/lib/asyncAction'

import compensationsCollection from 'modules/contracts/store/collections/compensations'
import type SupplementsFormStore from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/store'
import { fromToParams, range } from 'modules/payroll_bundle/components/Handoffs/dates'
import draftSupplementsCollection from 'modules/payroll_bundle/store/collections/draftSupplements'
import observationsCollection from 'modules/payroll_bundle/store/collections/observations'
import supplementObservations from 'modules/payroll_bundle/store/collections/supplementObservations'
import supplementsCollection from 'modules/payroll_bundle/store/collections/supplements'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

interface Params {
  date: moment.Moment
  frequency: string
  supplementsStore: SupplementsFormStore
  employeeIds: string[]
  currentPage?: number
}

// TODO: Mother of god, refactor this. Move it to the store and make it transactional
const useFetchData = ({ date, frequency, supplementsStore, employeeIds, currentPage }: Params) => {
  const [loading, setLoading] = useState<boolean>(true)
  const [isNotFound, setIsNotFound] = useState<boolean>(false)
  const dateValue = date.toISOString()

  const { loading: loadingAuthorizations } = useAsyncData(async () => {
    await Promise.all([
      policyAuthorizations.authorizePolicies('payroll_supplements.supplements.read'),
      policyAuthorizations.authorizePolicies('payroll_supplements.observations.read'),
    ])
  }, [])

  const canSeeSupplements = policyAuthorizations.for('payroll_supplements.supplements.read')
  const canSeeObservations = policyAuthorizations.for('payroll_supplements.observations.read')

  const fetchSupplements = async (newEmployeeIds: string[] = []) => {
    const isUsingBatchedSupplements =
      (newEmployeeIds.length > 0 || employeeIds.length > 0) && currentPage

    const newEmployeeIdsToLoad = newEmployeeIds.length ? newEmployeeIds : employeeIds
    const data = {
      ...fromToParams(range(date, frequency)),
      frequency,
      ...(isUsingBatchedSupplements ? { employee_ids: newEmployeeIdsToLoad } : {}),
    }

    setLoading(true)

    const { success } = await asyncAction(
      async () => {
        const [
          supplementsData,
          draftSupplementsData,
          observationsData,
          supplementObservationsData,
        ] = await Promise.all([
          canSeeSupplements ? supplementsCollection.fetch({ data }) : Promise.resolve([]),
          canSeeSupplements
            ? draftSupplementsCollection.fetch({
                data,
              })
            : Promise.resolve([]),

          canSeeObservations
            ? observationsCollection.fetch({
                data,
              })
            : Promise.resolve([]),
          supplementObservations.fetch({ data }),
        ])

        const allSupplements = [
          ...supplementsData,
          ...(Array.isArray(draftSupplementsData) ? draftSupplementsData : []),
        ]

        runInAction(() => {
          supplementsStore.buildForms(allSupplements)
          supplementsStore.buildObservationForms(observationsData)
          supplementsStore.buildSupplementObservationForms(supplementObservationsData)
        })

        const ids = allSupplements
          .filter((s) => s.contracts_compensation_id)
          .map((s) => s.contracts_compensation_id)

        // Due to reaching URL limit size
        await compensationsCollection.batchFetchByIds(ids, 100)
      },
      {
        successFlash: false,
        errorFlash: true,
      }
    )
    setLoading(false)

    if (!success) {
      setIsNotFound(true)
    }
  }

  useEffect(() => {
    fetchSupplements()
  }, [frequency, dateValue, loadingAuthorizations, currentPage]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    loading,
    fetchSupplements,
    isNotFound,
  }
}

export default useFetchData
